/**
 * Browser sign-in gate — stale localStorage authed flags never count as signed in.
 * Server-verified session required before skipping AuthView on /login, /signin, or /app.
 */

import { CLOUD_API_URL } from '../config/serverConfig';
import { isSessionDead } from './sessionDead';
import { isNativeApp } from '../utils/platform';

const LS_AUTH = 'maestro_auth';
const LS_AUTHED_ONCE = 'maestro_authed_once_v1';
const SS_REFRESH_FAILED = 'maestro_refresh_failed';
const SS_CONNECTOR_AUTH_JSON = 'maestro_connector_has_auth_json';

export type BrowserSessionProbe = 'idle' | 'checking' | 'verified' | 'invalid';

export function isBrowserClient(): boolean {
  return typeof window !== 'undefined' && !isNativeApp();
}

export function hasSignInIntent(searchParams: URLSearchParams | { get: (key: string) => string | null }): boolean {
  return searchParams.get('signin') === '1';
}

export function hasStoredAccessToken(): boolean {
  if (isSessionDead()) return false;
  try {
    const raw = localStorage.getItem(LS_AUTH);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { accessToken?: string };
    return !!parsed?.accessToken;
  } catch {
    return false;
  }
}

/** Clear inherited-profile authed flags when the browser session is not server-valid. */
export function purgeStaleBrowserSignInFlags(): void {
  if (typeof localStorage !== 'undefined') {
    try {
      localStorage.removeItem(LS_AUTH);
      localStorage.removeItem(LS_AUTHED_ONCE);
    } catch { /* ignore */ }
  }
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.removeItem(SS_REFRESH_FAILED);
      sessionStorage.removeItem(SS_CONNECTOR_AUTH_JSON);
    } catch { /* ignore */ }
  }
}

/** One cheap authenticated call — stale tokens alone never pass. */
export async function probeBrowserSessionVerified(): Promise<boolean> {
  if (!hasStoredAccessToken()) return false;
  let token: string | null = null;
  try {
    const raw = localStorage.getItem(LS_AUTH);
    token = raw ? (JSON.parse(raw) as { accessToken?: string }).accessToken ?? null : null;
  } catch {
    return false;
  }
  if (!token) return false;
  try {
    const res = await fetch(`${CLOUD_API_URL}/api/auth/user`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'omit',
    });
    return res.ok;
  } catch {
    return false;
  }
}

export async function invalidateStaleBrowserSession(): Promise<void> {
  purgeStaleBrowserSignInFlags();
  const { applyBootTerminalSignInRequired } = await import('../components/AuthView');
  applyBootTerminalSignInRequired();
}
